import React, { useState, useEffect, useCallback, useRef } from "react";
import JSZip from "jszip";
import { useBluelagoon, Bluelagoon } from "@bluelagoon";
import ComponentsDashboardNav from "./ComponentsDashboardNav";
import AppPreviewIframe from "./AppPreviewIframe";
import CodeEditorPanel from "./CodeEditorPanel";
import VersionHistoryPanel from "./VersionHistoryPanel";
import OverviewPanel from "./OverviewPanel";
import UsersPanel from "./UsersPanel";
import DomainsPanel from "./DomainsPanel";
import AuthenticationPanel from "./AuthenticationPanel";
import DataSecurityPanel from "./DataSecurityPanel";
import DatabaseSharingPanel from "./DatabaseSharingPanel";
import FunctionSharingPanel from "./FunctionSharingPanel";
import IntegrationsPanel from "./IntegrationsPanel";
import SecretsPanel from "./SecretsPanel";
import SettingsPanel from "./SettingsPanel";
import APIsPanel from "./APIsPanel";
import EnvironmentsPanel from "./EnvironmentsPanel";
import ImportProjectPanel from "./ImportProjectPanel";
import CodeScanningPanel from "./CodeScanningPanel";
import TestingPanel from "./TestingPanel";
import ActivityPanel from "./ActivityPanel";
import MarkdownPreviewPanel from "./MarkdownPreviewPanel";
import { useWorkflow } from "../../context/WorkflowContext";
import { useWorkflowGeneration } from "../../context/WorkflowGenerationContext";
import { useWorkflowExecution } from "../../context/WorkflowExecutionContext";
import { useChatContext } from "../ChatContext";
import { useEditor } from "../EditorContext";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import { Eye, X } from "lucide-react";
import { usePropLogger } from "../../hooks/usePropLogger";
import useWorkflowPreviewToken from "../../hooks/useWorkflowPreviewToken";
import {
    appendRequestLog,
    appendConsoleLog,
    createConsoleLogEntry,
} from "./appPreviewPayloadUtils";

export default function AppPreviewDashboard({ handleErrorLog }) {
    const { t } = useTranslation();
    const { isFeatureEnabled } = useAuth();

    // Use ChatContext for shared state
    const {
        appViewMode: activeView,
        previewDevice,
        components,
        fetchComponents,
        isComponentsLoading: loading,
        componentsError: error,
        confirm,
        selectedPageId,
        setIframePageId,
        selectedRightPanel,
        setSelectedRightPanel,
        navigateToOverview,
        navigateToUsers,
        navigateToDomains,
        navigateToAuthentication,
        navigateToDataSecurity,
        navigateToDatabaseSharing,
        navigateToFunctionSharing,
        navigateToIntegrations,
        navigateToSecrets,
        navigateToSettings,
        navigateToAPIs,
        navigateToEnvironments,
        navigateToEditor,
        selectedEnvironment,
        navigateToVersionHistory,
        navigateToImport,
        navigateToCodeScanning,
        navigateToTesting,
        navigateToActivity,
        isCurrentEnvironmentLocked,
        environments,
        markdownPreview,
        closeMarkdownPreview,
        isSelectMode,
        setIsSelectMode,
        setComponentRef,
        viewAsUser,
        setViewAsUser,
        previewReloadKey,
    } = useChatContext();
    const { clearUnsavedChanges, fileStates } = useEditor();
    const { call: fetchComponentCode } = useBluelagoon({ method: "getComponentCode" });

    // usePropLogger({ components }, "AppPreviewDashboard");

    const [dashboardSection, setDashboardSection] = useState("menu"); // "menu", "editor", or "folder-components"
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedComponent, setSelectedComponent] = useState(null);
    const [requireLogin, setRequireLogin] = useState(false);
    const [requiresPartialLogin, setRequiresPartialLogin] = useState(false);
    const [allowSelfSignup, setAllowSelfSignup] = useState(false);
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [ssoEnabled, setSsoEnabled] = useState(false);
    const [ssoRoleMappings, setSsoRoleMappings] = useState({});
    const [ssoClaimMappings, setSsoClaimMappings] = useState({});
    const [isFunctionAPIEnabled, setIsFunctionAPIEnabled] = useState(false);
    const [isProductionLocked, setIsProductionLocked] = useState(false);
    const [isInstallableWebApp, setIsInstallableWebApp] = useState(false);
    const [isOfflineEnabled, setIsOfflineEnabled] = useState(false);
    const [isDeepLinkEnabled, setIsDeepLinkEnabled] = useState(false);
    const [isGitHubEnabled, setIsGitHubEnabled] = useState(false);
    const [gitHubRepo, setGitHubRepo] = useState("");
    const [isGitHubConnected, setIsGitHubConnected] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const isMobilePreview = previewDevice === "mobile";

    // New file/folder dialog state
    const [newFileDialog, setNewFileDialog] = useState({
        isOpen: false,
        folderPath: null,
    });
    const [newFolderDialog, setNewFolderDialog] = useState({
        isOpen: false,
        parentPath: null,
    });
    const [deleteDialog, setDeleteDialog] = useState({
        isOpen: false,
        filePath: null,
        fileName: null,
        fileId: null,
    });

    // Error handling state
    const [reloadTrigger, setReloadTrigger] = useState(() => Date.now());

    // Version history state
    const [versionHistory, setVersionHistory] = useState([]);
    const [versionPage, setVersionPage] = useState(1);
    const [hasMoreVersions, setHasMoreVersions] = useState(true);
    const VERSIONS_PER_PAGE = 5;

    // API call for updating workflow visibility settings
    const { call: updateWorkflowVisibility, isLoading: isUpdatingVisibility } =
        useBluelagoon({ method: "updateWorkflow" });

    // API call for creating new components
    const { call: createComponentApi, isLoading: isCreatingComponent } =
        useBluelagoon({ method: "createComponent" });

    // API call for deleting components
    const { call: deleteComponentApi, isLoading: isDeletingComponent } =
        useBluelagoon({ method: "deleteComponent" });

    // API call for updating components (used for renaming)
    const { call: updateComponentApi } = useBluelagoon({
        method: "updateComponent",
    });

    // API call for logging export audit
    const { call: logExportAuditCall } = useBluelagoon({
        method: "logExportAudit",
    });

    const { call: updateWorkflowCall, isLoading: isLoadingUpdateWorkflow } =
        useBluelagoon({
            method: "updateWorkflow",
        });

    // API call for updating workflow step version metadata
    const { call: updateWorkflowStepVersion } = useBluelagoon({
        method: "updateWorkflowStepVersion",
    });

    // API call for setting production lock
    const { call: setProductionLockApi, isLoading: isTogglingProductionLock } =
        useBluelagoon({
            method: "setProductionLock",
        });

    // API call for getting GitHub auth URL
    const { call: getGitHubAuthUrlCall } = useBluelagoon({
        method: "getGitHubAuthUrl",
    });

    // Get version history functions from context
    const {
        getWorkflowStepVersions,
        isLoadingStepVersions,
        workflow,
        refetchWorkflow,
    } = useWorkflow();

    // Short-lived workflow-scoped JWT for previewing the user's own workflow.
    // Replaces the practice of passing the platform Bearer in the URL as
    // x_token_auth. `issueLongLivedToken` mints a 7-day variant for the
    // detached new-tab session (parent can't refresh it).
    const { token: previewToken, issueLongLivedToken } =
        useWorkflowPreviewToken(workflow?.id);

    // Sync requireLogin and allowSelfSignup with workflow settings
    useEffect(() => {
        if (workflow?.requiresLogin !== undefined) {
            setRequireLogin(workflow.requiresLogin);
        }
        if (workflow?.requiresPartialLogin !== undefined) {
            setRequiresPartialLogin(workflow.requiresPartialLogin);
        }
        if (workflow?.allowSelfSignup !== undefined) {
            setAllowSelfSignup(workflow.allowSelfSignup);
        }
        if (workflow?.mfaEnabled !== undefined) {
            setMfaEnabled(workflow.mfaEnabled);
        }
        if (workflow?.ssoEnabled !== undefined) {
            setSsoEnabled(workflow.ssoEnabled);
        }
        if (workflow?.ssoRoleMappings !== undefined) {
            setSsoRoleMappings(workflow.ssoRoleMappings || {});
        }
        if (workflow?.ssoClaimMappings !== undefined) {
            setSsoClaimMappings(workflow.ssoClaimMappings || {});
        }
        if (workflow?.isFunctionAPIEnabled !== undefined) {
            setIsFunctionAPIEnabled(workflow.isFunctionAPIEnabled);
        }
        if (workflow?.isProductionLocked !== undefined) {
            setIsProductionLocked(workflow.isProductionLocked);
        }
        if (workflow?.isInstallableWebApp !== undefined) {
            setIsInstallableWebApp(workflow.isInstallableWebApp);
        }
        if (workflow?.isOfflineEnabled !== undefined) {
            setIsOfflineEnabled(workflow.isOfflineEnabled);
        }
        if (workflow?.isDeepLinkEnabled !== undefined) {
            setIsDeepLinkEnabled(workflow.isDeepLinkEnabled);
        }
        if (workflow?.isGitHubEnabled !== undefined) {
            setIsGitHubEnabled(workflow.isGitHubEnabled);
        }
        if (workflow?.gitHubRepo !== undefined) {
            setGitHubRepo(workflow.gitHubRepo || "");
        }
        if (workflow?.gitHubIntegrationId) {
            setIsGitHubConnected(true);
        } else {
            setIsGitHubConnected(false);
        }
    }, [
        workflow?.requiresLogin,
        workflow?.requiresPartialLogin,
        workflow?.allowSelfSignup,
        workflow?.mfaEnabled,
        workflow?.ssoEnabled,
        workflow?.ssoRoleMappings,
        workflow?.ssoClaimMappings,
        workflow?.isFunctionAPIEnabled,
        workflow?.isProductionLocked,
        workflow?.isInstallableWebApp,
        workflow?.isOfflineEnabled,
        workflow?.isDeepLinkEnabled,
        workflow?.isGitHubEnabled,
        workflow?.gitHubRepo,
        workflow?.gitHubIntegrationId,
    ]);

    const {
        rollbackWorkflowStep,
        isLoadingRollback,
        isGeneratingCode,
        updateCurrentPageContext,
        setRequestSuccessLogs,
        activeStep,
        missingSecretWarnings,
        setMissingSecretWarnings,
        sendHelperMessage,
        appendHelperMessage,
        onClearHelperMessages,
    } = useWorkflowGeneration();

    // Get workflow execution context for logging
    const { setStepExecutionLogs } = useWorkflowExecution();

    // Blueprint subscription state
    const [maintainableBlueprint, setMaintainableBlueprint] = useState(null);
    const [blueprintSubscription, setBlueprintSubscription] = useState(null);
    const { call: fetchMySubscriptions } = useBluelagoon({ method: "getMySubscriptions" });
    const { call: fetchMaintainableBlueprint } = useBluelagoon({
        method: "getMaintainableBlueprintByWorkflow",
    });

    // Fetch maintainable blueprint authored from this workflow + subscription status
    const refetchMaintainableBlueprint = useCallback(() => {
        if (!workflow?.id) return;
        fetchMaintainableBlueprint({ workflowId: workflow.id })
            .then((res) => setMaintainableBlueprint(res?.data || null))
            .catch(() => {});
    }, [workflow?.id]);

    useEffect(() => {
        if (!workflow?.id) return;
        refetchMaintainableBlueprint();
        fetchMySubscriptions({})
            .then((res) => {
                const subs = res?.data || res;
                if (Array.isArray(subs)) {
                    const sub = subs.find((s) => s.workflowId === workflow.id);
                    setBlueprintSubscription(sub || null);
                }
            })
            .catch(() => {});
    }, [workflow?.id]);

    // Extract stepId and currentVersion for useEffect dependency
    const stepId = activeStep || workflow?.steps?.[0]?.id;
    const currentVersion = workflow?.steps?.[0]?.currentVersion;

    // Derive the version to display in the preview based on the environment
    const hasCustomLoginPage = React.useMemo(
        () =>
            components?.some(
                (c) =>
                    (c.name === "Login" ||
                        c.name === "Login.jsx" ||
                        c.name === "Login.js") &&
                    c.type === "PAGE"
            ) ?? false,
        [components]
    );

    const displayVersion = React.useMemo(() => {
        if (!selectedEnvironment || selectedEnvironment === "production") {
            return currentVersion;
        }
        const env = environments?.find((e) => e.name === selectedEnvironment);
        return env?.currentWorkflowStepVersionId || currentVersion;
    }, [selectedEnvironment, environments, currentVersion]);

    const lastReloadedStateRef = React.useRef({
        version: displayVersion,
        environment: selectedEnvironment,
    });

    // Determine component server URL based on environment
    const { componentServerUrl, useCustomDomain } = React.useMemo(() => {
        const currentEnvironment = selectedEnvironment
            ? environments.find((e) => e.name === selectedEnvironment)
            : null;
        return Bluelagoon.getComponentServerUrl(
            workflow?.internalDomain,
            currentEnvironment?.customDomain
        );
    }, [workflow?.internalDomain, selectedEnvironment, environments]);

    // Handle AI suggestions from infrastructure (e.g., pagination needed, rate limits)
    // Shows a toast to the user and stores the suggestion in logs for AI context
    const aiSuggestionDedupeRef = React.useRef(new Set());
    const handleAiSuggestion = useCallback(
        (message, metadata = {}) => {
            // Deduplicate suggestions by message within the session
            const key = `${metadata?.type || ""}:${metadata?.tableName || ""}`;
            if (aiSuggestionDedupeRef.current.has(key)) return;
            aiSuggestionDedupeRef.current.add(key);

            const suggestion = metadata?.suggestion || message;
            toast.info(suggestion, { duration: 8000 });
        },
        []
    );

    // Handle human-facing suggestions from infrastructure (e.g., pagination improvements)
    // Shows a helper message with an "Apply" button that sends the suggestion to the AI
    const humanSuggestionDedupeRef = React.useRef(new Set());
    useEffect(() => {
        if (!onClearHelperMessages) return;
        return onClearHelperMessages(() => {
            humanSuggestionDedupeRef.current.clear();
        });
    }, [onClearHelperMessages]);
    const handleHumanSuggestion = useCallback(
        (message, metadata = {}) => {
            const key = `${metadata?.type || ""}:${metadata?.entity || metadata?.tableName || ""}:${message}`;
            if (humanSuggestionDedupeRef.current.has(key)) return;
            humanSuggestionDedupeRef.current.add(key);

            const suggestion = metadata?.suggestion || message;
            const showApply = metadata?.actionable === true;
            appendHelperMessage(
                suggestion,
                showApply ? "applySuggestion:" + suggestion : "",
                showApply ? t("common.apply") : "",
                metadata?.data || null
            );
        },
        [appendHelperMessage, t]
    );

    // Function to add console logs to stepExecutionLogs
    const addConsoleLogToStepExecution = useCallback(
        (logType, message, timestamp = Date.now()) => {
            if (!stepId || !setStepExecutionLogs) return;

            const logEntry = createConsoleLogEntry(
                logType,
                message,
                timestamp
            );

            setStepExecutionLogs((prev) => ({
                ...prev,
                [stepId]: appendConsoleLog(prev[stepId] || [], logEntry),
            }));
        },
        [stepId, setStepExecutionLogs]
    );

    // Function to add axios responses (success or error) to stepExecutionLogs
    const addAxiosResponseToStepExecution = useCallback(
        (responseData, timestamp = Date.now()) => {
            if (!stepId || !setRequestSuccessLogs) return;

            setRequestSuccessLogs((prev) => {
                return appendRequestLog(prev, responseData, timestamp);
            });
        },
        [stepId, setRequestSuccessLogs]
    );

    // Group components by folder (extract directory from filePath)
    const { componentsByFolder, folders, rootComponents } =
        React.useMemo(() => {
            const comps = components || [];
            const grouped = {};
            const root = [];

            comps.forEach((component) => {
                // Extract directory path from filePath
                if (component.filePath) {
                    const pathParts = component.filePath.split("/");
                    // If there's more than just the filename, it's in a folder
                    if (pathParts.length > 1) {
                        const folder = pathParts.slice(0, -1).join("/");
                        if (folder && folder !== "") {
                            if (!grouped[folder]) {
                                grouped[folder] = [];
                            }
                            grouped[folder].push(component);
                            return;
                        }
                    }
                }
                // If no folder or just filename, add to root
                root.push(component);
            });

            return {
                componentsByFolder: grouped,
                folders: Object.keys(grouped).sort(),
                rootComponents: root,
            };
        }, [components]);

    // Handle API errors
    useEffect(() => {
        if (error) {
            const errorMessage =
                error.message ||
                t("appPreview.errorFetchingComponents") ||
                "Failed to fetch components";
            console.error("Component fetch error:", error);
            toast.error(errorMessage);
        }
    }, [error, t]);

    // Helper function to reload iframe
    const reloadIframe = useCallback(() => {
        setReloadTrigger(Date.now());
    }, []);

    // External reload signal: anything in the tree (e.g. RollbackButton) can
    // call requestPreviewReload() on ChatContext to force the iframe to
    // remount even when stepId / version / environment haven't changed —
    // needed for in-place mutations like rollback that flip isActive on the
    // current step version without bumping its number.
    const lastSeenPreviewReloadKeyRef = useRef(previewReloadKey);
    useEffect(() => {
        if (previewReloadKey === lastSeenPreviewReloadKeyRef.current) return;
        lastSeenPreviewReloadKeyRef.current = previewReloadKey;
        reloadIframe();
    }, [previewReloadKey, reloadIframe]);

    // Clear logs when version changes
    useEffect(() => {
        if (isGeneratingCode) return;
        setRequestSuccessLogs([]);
    }, [currentVersion, isGeneratingCode, setRequestSuccessLogs]);

    // Signal iframe to reload when relevant changes occur
    useEffect(() => {
        // If we are generating, don't trigger reloads yet
        if (isGeneratingCode) return;

        // The iframe has a key prop that includes stepId, environment and version.
        // This means it will naturally remount and reload when any of these change.
        // We only need to MANUALLY call reloadIframe() if we want to force a refresh
        // when these haven't changed (e.g., when generation finishes but version is the same,
        // or when componentServerUrl changes).

        const stateChanged =
            lastReloadedStateRef.current.version !== displayVersion ||
            lastReloadedStateRef.current.environment !== selectedEnvironment;

        if (stateChanged) {
            // State changed, React will reload the iframe via key prop.
            // Just update our ref.
            lastReloadedStateRef.current = {
                version: displayVersion,
                environment: selectedEnvironment,
            };
            return;
        }

        // If nothing changed in the URL/Key, but code generation just finished,
        // we might need a manual reload.
        reloadIframe();
    }, [
        stepId,
        componentServerUrl,
        useCustomDomain,
        displayVersion,
        selectedEnvironment,
        isGeneratingCode,
        reloadIframe,
    ]);

    // Close mobile sidebar when view changes
    useEffect(() => {
        setIsMobileSidebarOpen(false);
    }, [activeView]);

    // Sync current page context for AI awareness
    useEffect(() => {
        if (!updateCurrentPageContext) return;

        // Find the selected page/component details
        let pageInfo = {
            selectedPageId: selectedPageId,
            selectedPageName: null,
            selectedPageFilePath: null,
            selectedRightPanel: selectedRightPanel,
            appViewMode: activeView,
        };

        // If a page is selected, get its details
        if (selectedPageId && components) {
            const selectedPage = components.find(
                (c) => c.id === selectedPageId
            );
            if (selectedPage) {
                pageInfo.selectedPageName = selectedPage.name;
                pageInfo.selectedPageFilePath = selectedPage.filePath;
            }
        }

        // If in editor mode with a selected component, use that
        if (selectedComponent) {
            pageInfo.selectedPageId = selectedComponent.id;
            pageInfo.selectedPageName = selectedComponent.name;
            pageInfo.selectedPageFilePath = selectedComponent.filePath;
        }

        updateCurrentPageContext(pageInfo);
    }, [
        selectedPageId,
        selectedRightPanel,
        activeView,
        selectedComponent,
        components,
        updateCurrentPageContext,
    ]);

    // Handle element selected from visual editor (iframe)
    const handleElementSelected = useCallback(
        (elementData) => {
            // Match the selected element's component name to our components list
            const matchedComponent = elementData.componentName
                ? components?.find(
                      (c) =>
                          c.name === elementData.componentName ||
                          c.filePath
                              ?.replace(/\.[^/.]+$/, "")
                              .split("/")
                              .pop() === elementData.componentName
                  )
                : null;

            setComponentRef({
                componentId: matchedComponent?.id || null,
                componentName:
                    matchedComponent?.name ||
                    elementData.componentName ||
                    elementData.tagName,
                filePath: matchedComponent?.filePath || null,
                elementTag: elementData.tagName,
                elementSelector: elementData.id
                    ? `#${elementData.id}`
                    : elementData.className
                    ? `.${elementData.className.split(" ")[0]}`
                    : null,
                elementText: elementData.textContent,
            });

            // Exit select mode after selection
            setIsSelectMode(false);
        },
        [components, setComponentRef, setIsSelectMode]
    );

    const handleComponentSelect = (component) => {
        setSelectedComponent(component);
        setSelectedRightPanel(null);
        setIsMobileSidebarOpen(false); // Close mobile sidebar
    };

    const handleBackToMenu = () => {
        setDashboardSection("menu");
        setSelectedFolder(null);
        setSelectedComponent(null);
        setSelectedRightPanel(null);
        setIsMobileSidebarOpen(false); // Close mobile sidebar
    };

    const handleBackToEditor = React.useCallback(() => {
        if (selectedFolder) {
            const pathParts = selectedFolder.split("/");
            if (pathParts.length > 1) {
                // Navigate up one level
                setSelectedFolder(pathParts.slice(0, -1).join("/"));
            } else {
                // Back to root editor view from a top-level folder
                setSelectedFolder(null);
                setDashboardSection("editor");

                // Silently refresh components list in the background
                if (stepId) {
                    fetchComponents();
                }
            }
        } else {
            // Should not be called if already at root, but as a fallback
            setDashboardSection("editor");

            // Silently refresh components list in the background
            if (stepId) {
                fetchComponents();
            }
        }
        setSelectedComponent(null);
        setSelectedRightPanel(null);
        setIsMobileSidebarOpen(false); // Close mobile sidebar
    }, [stepId, fetchComponents, navigateToEditor]);

    // Handle component updated - refresh components and preview
    const handleComponentUpdated = React.useCallback(async () => {
        if (stepId) {
            // Refresh components list
            fetchComponents();

            // Refetch workflow to get the updated currentVersion
            // This will trigger the iframe reload automatically via the useEffect that watches currentVersion
            if (refetchWorkflow) {
                await refetchWorkflow();
            }
        }
    }, [stepId, fetchComponents, refetchWorkflow]);

    const handleEnterEditor = React.useCallback(() => {
        setDashboardSection("editor");
        setSelectedFolder(null); // Ensure we start at the root
        navigateToEditor();
        setIsMobileSidebarOpen(false); // Close mobile sidebar

        // Silently refresh components list in the background
        if (stepId) {
            fetchComponents();
        }
    }, [stepId, fetchComponents, navigateToEditor]);

    // Wrapper handlers that call context navigation and close mobile sidebar
    const handleEnterOverview = React.useCallback(() => {
        navigateToOverview();
        setIsMobileSidebarOpen(false);
    }, [navigateToOverview]);

    const handleEnterUsers = React.useCallback(() => {
        navigateToUsers();
        setIsMobileSidebarOpen(false);
    }, [navigateToUsers]);

    const handleEnterDomains = React.useCallback(() => {
        navigateToDomains();
        setIsMobileSidebarOpen(false);
    }, [navigateToDomains]);

    const handleEnterAuthentication = React.useCallback(() => {
        navigateToAuthentication();
        setIsMobileSidebarOpen(false);
    }, [navigateToAuthentication]);

    const handleEnterDataSecurity = React.useCallback(() => {
        navigateToDataSecurity();
        setIsMobileSidebarOpen(false);
    }, [navigateToDataSecurity]);

    const handleEnterDatabaseSharing = React.useCallback(() => {
        navigateToDatabaseSharing();
        setIsMobileSidebarOpen(false);
    }, [navigateToDatabaseSharing]);

    const handleEnterFunctionSharing = React.useCallback(() => {
        navigateToFunctionSharing();
        setIsMobileSidebarOpen(false);
    }, [navigateToFunctionSharing]);

    const handleEnterSecrets = React.useCallback(() => {
        navigateToSecrets();
        setIsMobileSidebarOpen(false);
    }, [navigateToSecrets]);

    const handleEnterSettings = React.useCallback(() => {
        navigateToSettings();
        setIsMobileSidebarOpen(false);
    }, [navigateToSettings]);

    const handleEnterAPIs = React.useCallback(() => {
        navigateToAPIs();
        setIsMobileSidebarOpen(false);
    }, [navigateToAPIs]);

    const handleEnterEnvironments = React.useCallback(() => {
        navigateToEnvironments();
        setIsMobileSidebarOpen(false);
    }, [navigateToEnvironments]);

    const handleEnterImport = React.useCallback(() => {
        navigateToImport();
        setIsMobileSidebarOpen(false);
    }, [navigateToImport]);

    const handleEnterCodeScanning = React.useCallback(() => {
        navigateToCodeScanning();
        setIsMobileSidebarOpen(false);
    }, [navigateToCodeScanning]);

    const handleEnterTesting = React.useCallback(() => {
        navigateToTesting();
        setIsMobileSidebarOpen(false);
    }, [navigateToTesting]);

    const handleEnterIntegrations = React.useCallback(() => {
        navigateToIntegrations();
        setIsMobileSidebarOpen(false);
    }, [navigateToIntegrations]);

    const handleFolderSelect = React.useCallback((folder) => {
        setSelectedFolder(folder);
        setDashboardSection("folder-components");
        setSelectedComponent(null);
        setSelectedRightPanel(null);
        setIsMobileSidebarOpen(false); // Close mobile sidebar
    }, []);

    // Version history functions
    const handleViewVersionHistory = React.useCallback(
        async (page = 1) => {
            if (!stepId) return;

            // Ensure page is a number (handle event objects from onClick)
            const pageNum = typeof page === "number" ? page : 1;

            if (pageNum === 1) {
                navigateToVersionHistory();
                setVersionHistory([]); // Clear existing history when starting fresh
            }

            try {
                const offset = (pageNum - 1) * VERSIONS_PER_PAGE;
                const history = await getWorkflowStepVersions(stepId, {
                    limit: VERSIONS_PER_PAGE,
                    offset: offset,
                });

                if (history?.versions) {
                    if (pageNum === 1) {
                        setVersionHistory(history.versions);
                    } else {
                        setVersionHistory((prev) => [
                            ...prev,
                            ...history.versions,
                        ]);
                    }

                    // Use hasMore from API response if available, otherwise fallback to length check
                    if (
                        history.pagination &&
                        typeof history.pagination.hasMore === "boolean"
                    ) {
                        setHasMoreVersions(history.pagination.hasMore);
                    } else {
                        setHasMoreVersions(
                            history.versions.length === VERSIONS_PER_PAGE
                        );
                    }

                    setVersionPage(pageNum);
                }
            } catch (error) {
                console.error("Error fetching version history:", error);
                toast.error(
                    t("stepsExecutionPanel.failedToFetchVersionHistory") ||
                        "Failed to fetch version history"
                );
            }
        },
        [stepId, t, getWorkflowStepVersions, navigateToVersionHistory]
    );

    const handleLoadMoreVersions = React.useCallback(() => {
        if (hasMoreVersions && !isLoadingStepVersions) {
            handleViewVersionHistory(versionPage + 1);
        }
    }, [
        hasMoreVersions,
        isLoadingStepVersions,
        versionPage,
        handleViewVersionHistory,
    ]);

    const handleRollback = React.useCallback(
        async (versionId) => {
            if (!stepId) {
                return;
            }

            try {
                await confirm({
                    title:
                        t("stepsExecutionPanel.rollbackStep") ||
                        "Rollback to this version?",
                    description:
                        t("stepsExecutionPanel.rollbackStepDescription") ||
                        "This will restore all components to their state at this version. Your current version will be preserved in history.",
                    confirmText:
                        t("stepsExecutionPanel.rollback") || "Rollback",
                    cancelText: t("common.cancel") || "Cancel",
                    isLoading: isLoadingRollback,
                });

                const rollbackResult = await rollbackWorkflowStep(
                    stepId,
                    versionId,
                    selectedEnvironment
                );

                setMissingSecretWarnings(
                    rollbackResult?.missingSecretWarnings || []
                );

                // Discard any unsaved editor state so synced components take effect
                clearUnsavedChanges();

                // Refresh components so the editor shows rolled-back code
                fetchComponents();
                reloadIframe();

                // Refresh workflow state (currentVersion may have changed)
                if (refetchWorkflow) {
                    refetchWorkflow();
                }

                // Refresh version history
                const history = await getWorkflowStepVersions(stepId, {
                    limit: 20,
                });
                if (history?.versions) {
                    setVersionHistory(history.versions);
                }

                toast.success(
                    t("stepsExecutionPanel.rollbackSuccessful") ||
                        "Version restored successfully"
                );
            } catch (error) {
                toast.error(
                    t("stepsExecutionPanel.rollbackCanceled") ||
                        "Rollback canceled"
                );
            }
        },
        [
            stepId,
            confirm,
            rollbackWorkflowStep,
            setMissingSecretWarnings,
            clearUnsavedChanges,
            fetchComponents,
            reloadIframe,
            refetchWorkflow,
            getWorkflowStepVersions,
            isLoadingRollback,
            selectedEnvironment,
            t,
        ]
    );

    const handleUpdateVersion = async (versionId, updates) => {
        if (!stepId) return false;

        try {
            await updateWorkflowStepVersion({
                versionId,
                ...updates,
            });

            // Refresh version history keeping the same amount of versions loaded
            const currentCount = versionHistory.length || VERSIONS_PER_PAGE;
            const limit = Math.max(currentCount, VERSIONS_PER_PAGE);

            const history = await getWorkflowStepVersions(stepId, {
                limit: limit,
                offset: 0,
            });

            if (history?.versions) {
                setVersionHistory(history.versions);

                // Update pagination state if available
                if (
                    history.pagination &&
                    typeof history.pagination.hasMore === "boolean"
                ) {
                    setHasMoreVersions(history.pagination.hasMore);
                } else {
                    setHasMoreVersions(history.versions.length >= limit);
                }
            }

            toast.success(t("common.saved") || "Saved");
            return true;
        } catch (error) {
            console.error("Error updating version:", error);
            toast.error(t("common.error") || "Error updating version");
            return false;
        }
    };

    // Get databases from workflow execution context
    const { workflowDatabases } = useWorkflowExecution();

    const databases = React.useMemo(() => {
        // Databases follow the convention:
        //   - Production: named "${workflowName}" (no "envName-" prefix)
        //   - Other envs: named "${envName}-${workflowName}"
        // See apps/api/src/modules/databases/services/schemaHelpers.js
        const activeDatabases =
            workflowDatabases?.filter(
                (db) => db.isActiveWorkflowDatabase === true
            ) || [];
        if (activeDatabases.length === 0) return [];

        const workflowName = workflow?.name || "";
        const sanitize = (name) =>
            (name || "")
                .toLowerCase()
                .replace(/\s+/g, "_")
                .replace(/[^a-z0-9_-]/g, "");
        const sanitizedWorkflowName = sanitize(workflowName);
        const isProduction =
            !selectedEnvironment || selectedEnvironment === "production";

        if (isProduction) {
            const prodDb =
                activeDatabases.find(
                    (db) =>
                        db.name === workflowName ||
                        db.name === sanitizedWorkflowName
                ) ||
                activeDatabases.find((db) => !db.name?.includes("-"));
            return prodDb ? [prodDb] : [];
        }

        const envPrefixRaw = `${selectedEnvironment}-${workflowName}`;
        const envPrefixSanitized = sanitize(envPrefixRaw);
        const envDb = activeDatabases.find(
            (db) => db.name === envPrefixRaw || db.name === envPrefixSanitized
        );
        return envDb ? [envDb] : [];
    }, [workflowDatabases, workflow?.name, selectedEnvironment]);

    // Handle visibility toggle
    const handleVisibilityToggle = async () => {
        if (!workflow?.id) return;

        try {
            const newValue = !requireLogin;
            const updates = {
                id: workflow.id,
                requiresLogin: newValue,
            };

            // If enabling global login, disable partial login
            if (newValue) {
                updates.requiresPartialLogin = false;
            }

            await updateWorkflowVisibility(updates);

            setRequireLogin(newValue);
            if (newValue) {
                setRequiresPartialLogin(false);
            }

            toast.success(
                newValue
                    ? t("appPreview.loginRequired") || "Login is now required"
                    : t("appPreview.loginNotRequired") ||
                          "Login is no longer required"
            );
        } catch (error) {
            console.error("Error updating visibility:", error);
            toast.error(
                t("appPreview.visibilityUpdateError") ||
                    "Failed to update visibility settings"
            );
        }
    };

    // Handle partial login toggle
    const handlePartialLoginToggle = async () => {
        if (!workflow?.id) return;

        try {
            const newValue = !requiresPartialLogin;
            const updates = {
                id: workflow.id,
                requiresPartialLogin: newValue,
            };

            // If enabling partial login, disable global login
            if (newValue) {
                updates.requiresLogin = false;
            }

            await updateWorkflowVisibility(updates);

            setRequiresPartialLogin(newValue);
            if (newValue) {
                setRequireLogin(false);
            }

            toast.success(
                t("appPreview.metadataUpdated") ||
                    "App information updated successfully"
            );
        } catch (error) {
            console.error("Error updating partial login:", error);
            toast.error(
                t("appPreview.visibilityUpdateError") ||
                    "Failed to update visibility settings"
            );
        }
    };

    // Handle self-signup toggle
    const handleSelfSignupToggle = async () => {
        if (!workflow?.id) return;

        try {
            const newValue = !allowSelfSignup;
            await updateWorkflowVisibility({
                id: workflow.id,
                allowSelfSignup: newValue,
            });
            setAllowSelfSignup(newValue);
            toast.success(
                newValue
                    ? t("appPreview.selfSignupEnabled") ||
                          "Self signup is now enabled"
                    : t("appPreview.selfSignupDisabled") ||
                          "Self signup is now disabled"
            );
        } catch (error) {
            console.error("Error updating self-signup:", error);
            toast.error(
                t("appPreview.selfSignupUpdateError") ||
                    "Failed to update self-signup settings"
            );
        }
    };

    // Handle MFA toggle
    const handleMfaToggle = async () => {
        if (!workflow?.id) return;

        try {
            const newValue = !mfaEnabled;
            await updateWorkflowVisibility({
                id: workflow.id,
                mfaEnabled: newValue,
            });
            setMfaEnabled(newValue);
            toast.success(
                newValue
                    ? t("appPreview.mfaEnabled") || "MFA is now enabled"
                    : t("appPreview.mfaDisabled") || "MFA is now disabled"
            );
        } catch (error) {
            console.error("Error updating MFA:", error);
            toast.error(
                t("appPreview.mfaUpdateError") || "Failed to update MFA settings"
            );
        }
    };

    // Handle SSO toggle
    const handleSSOToggle = async () => {
        if (!workflow?.id || !isFeatureEnabled("sso")) return;

        try {
            const newValue = !ssoEnabled;
            await updateWorkflowVisibility({
                id: workflow.id,
                ssoEnabled: newValue,
            });
            setSsoEnabled(newValue);
            toast.success(
                newValue ? "SSO is now enabled" : "SSO is now disabled"
            );
        } catch (error) {
            console.error("Error updating SSO:", error);
            toast.error("Failed to update SSO settings");
        }
    };

    // Handle SSO role mappings change
    const handleRoleMappingsChange = async (newMappings) => {
        if (!workflow?.id) return;

        try {
            await updateWorkflowVisibility({
                id: workflow.id,
                ssoRoleMappings: newMappings,
            });
            setSsoRoleMappings(newMappings);
            toast.success("SSO role mappings updated");
        } catch (error) {
            console.error("Error updating SSO role mappings:", error);
            toast.error("Failed to update SSO role mappings");
        }
    };

    // Handle SSO claim-to-field mappings change
    const handleClaimMappingsChange = async (newMappings) => {
        if (!workflow?.id) return;

        try {
            await updateWorkflowVisibility({
                id: workflow.id,
                ssoClaimMappings: newMappings,
            });
            setSsoClaimMappings(newMappings);
            toast.success("SSO claim mappings updated");
        } catch (error) {
            console.error("Error updating SSO claim mappings:", error);
            toast.error("Failed to update SSO claim mappings");
        }
    };

    // Handle Function API toggle
    const handleFunctionAPIToggle = async () => {
        if (!workflow?.id) return;

        try {
            const newValue = !isFunctionAPIEnabled;
            await updateWorkflowVisibility({
                id: workflow.id,
                isFunctionAPIEnabled: newValue,
            });
            setIsFunctionAPIEnabled(newValue);
            refetchWorkflow();
            toast.success(
                newValue
                    ? t("appPreview.functionAPIEnabled") ||
                          "Function API is now enabled"
                    : t("appPreview.functionAPIDisabled") ||
                          "Function API is now disabled"
            );
        } catch (error) {
            console.error("Error updating Function API:", error);
            toast.error(
                t("appPreview.functionAPIUpdateError") ||
                    "Failed to update Function API settings"
            );
        }
    };

    // Handle Production Lock toggle
    const handleProductionLockToggle = async (newLockState) => {
        if (!workflow?.id) return;

        try {
            await setProductionLockApi({
                workflowId: workflow.id,
                locked: newLockState,
            });
            setIsProductionLocked(newLockState);
            refetchWorkflow();
            toast.success(
                newLockState
                    ? t("environments.productionLock.lockSuccess") ||
                          "Production environment locked"
                    : t("environments.productionLock.unlockSuccess") ||
                          "Production environment unlocked"
            );
        } catch (error) {
            console.error("Error toggling production lock:", error);
            toast.error(
                t("environments.productionLock.error") ||
                    "Failed to update production lock"
            );
        }
    };

    // Handle Installable Web App toggle
    const handleInstallableWebAppToggle = async () => {
        if (!workflow?.id) return;

        try {
            const newValue = !isInstallableWebApp;
            const updates = { id: workflow.id, isInstallableWebApp: newValue };
            // If disabling PWA, also disable offline mode
            if (!newValue) {
                updates.isOfflineEnabled = false;
            }
            await updateWorkflowVisibility(updates);
            setIsInstallableWebApp(newValue);
            if (!newValue) {
                setIsOfflineEnabled(false);
            }
            refetchWorkflow();
            toast.success(
                newValue
                    ? t("appPreview.installableWebAppEnabled") ||
                          "Installable web app is now enabled"
                    : t("appPreview.installableWebAppDisabled") ||
                          "Installable web app is now disabled"
            );
        } catch (error) {
            console.error("Error updating installable web app:", error);
            toast.error(
                t("appPreview.installableWebAppUpdateError") ||
                    "Failed to update installable web app settings"
            );
        }
    };

    // Handle Offline Mode toggle
    const handleOfflineToggle = async () => {
        if (!workflow?.id) return;

        try {
            const newValue = !isOfflineEnabled;
            await updateWorkflowVisibility({
                id: workflow.id,
                isOfflineEnabled: newValue,
            });
            setIsOfflineEnabled(newValue);
            refetchWorkflow();
            toast.success(
                newValue
                    ? t("appPreview.offlineModeEnabled") ||
                          "Offline mode is now enabled"
                    : t("appPreview.offlineModeDisabled") ||
                          "Offline mode is now disabled"
            );
        } catch (error) {
            console.error("Error updating offline mode:", error);
            toast.error(
                t("appPreview.offlineModeUpdateError") ||
                    "Failed to update offline mode settings"
            );
        }
    };

    // Handle Deep Link toggle
    const handleDeepLinkToggle = async () => {
        if (!workflow?.id || !isFeatureEnabled("deepLinking")) return;

        try {
            const newValue = !isDeepLinkEnabled;
            await updateWorkflowVisibility({
                id: workflow.id,
                isDeepLinkEnabled: newValue,
            });
            setIsDeepLinkEnabled(newValue);
            refetchWorkflow();
            toast.success(
                newValue
                    ? t("appPreview.deepLinkEnabled") ||
                          "Deep linking is now enabled"
                    : t("appPreview.deepLinkDisabled") ||
                          "Deep linking is now disabled"
            );
        } catch (error) {
            console.error("Error updating deep linking:", error);
            toast.error(
                t("appPreview.deepLinkUpdateError") ||
                    "Failed to update deep linking settings"
            );
        }
    };

    // Handle Regression Testing toggle
    const handleRegressionTestingToggle = async () => {
        if (!workflow?.id) return;
        try {
            const newValue = !workflow.isRegressionTestingEnabled;
            await updateWorkflowVisibility({
                id: workflow.id,
                isRegressionTestingEnabled: newValue,
            });
            refetchWorkflow();
        } catch (error) {
            console.error("Error updating regression testing:", error);
        }
    };

    // Handle audit log for viewing API key
    const handleAuditKeyView = React.useCallback(() => {
        logExportAuditCall({
            resourceType: "api_key",
            resourceId: workflow?.id || "",
            resourceName: workflow?.name || "project",
            exportFormat: "view",
        }).catch(() => {});
    }, [logExportAuditCall, workflow?.id, workflow?.name]);

    // Handle GitHub settings change (repo)
    const handleGitHubSettingsChange = async (updates) => {
        if (!workflow?.id) return;

        if (updates.gitHubRepo !== undefined) {
            setGitHubRepo(updates.gitHubRepo);
        }

        try {
            await updateWorkflowVisibility({
                id: workflow.id,
                ...updates,
            });
            refetchWorkflow();
        } catch (error) {
            console.error("Error updating GitHub settings:", error);
            toast.error(
                t("appPreview.gitHubSettingsUpdateError") ||
                    "Failed to update GitHub settings"
            );
        }
    };

    // Handle GitHub OAuth connect (also enables GitHub integration if not already enabled)
    const handleGitHubConnect = async () => {
        if (!workflow?.id) return;

        try {
            if (!isGitHubEnabled) {
                await updateWorkflowVisibility({ id: workflow.id, isGitHubEnabled: true });
                setIsGitHubEnabled(true);
            }

            const result = await getGitHubAuthUrlCall({
                workflowId: workflow.id,
                currentUrl: window.location.href,
            });
            if (result?.data?.url) {
                window.location.href = result.data.url;
            } else {
                toast.error("Failed to get GitHub authorization URL");
            }
        } catch (error) {
            console.error("Error connecting to GitHub:", error);
            toast.error("Failed to connect to GitHub");
        }
    };

    // Handle open app in new tab. Mints a fresh long-lived (7-day) workflow
    // token per click so the detached tab stays signed in even after the
    // parent's iframe token expires. Never the platform Bearer in the URL.
    const handleOpenApp = async () => {
        if (!stepId) return;
        const basePath = useCustomDomain
            ? componentServerUrl
            : `${componentServerUrl}/apps/${stepId}`;
        const token = await issueLongLivedToken();
        const params = new URLSearchParams({
            v: String(currentVersion || Date.now()),
        });
        if (token) params.set("x_deep_link_token", token);
        window.open(`${basePath}?${params.toString()}`, "_blank");
    };

    // Handle update workflow metadata
    const handleUpdateWorkflowMetadata = React.useCallback(
        async (updates) => {
            try {
                await updateWorkflowCall({
                    id: workflow.id,
                    ...updates,
                });
                refetchWorkflow();
                toast.success(
                    t("appPreview.metadataUpdated") ||
                        "App information updated successfully"
                );
            } catch (error) {
                console.error("Error updating workflow metadata:", error);
                toast.error(
                    t("appPreview.metadataUpdateError") ||
                        "Failed to update app information"
                );
                // Re-throw the error so calling components can handle it
                throw error;
            }
        },
        [updateWorkflowCall, workflow.id, t, refetchWorkflow]
    );

    // Handle export project
    const handleExportProject = React.useCallback(async () => {
        if (!stepId || !components) {
            toast.error(
                t("appPreview.noComponentsToExport") ||
                    "No components to export"
            );
            return;
        }

        const toastId = toast.loading(
            t("appPreview.exportingProject") || "Exporting project..."
        );

        try {
            const zip = new JSZip();
            const existingComponents = components || [];

            // Fetch code for all components that don't have it cached (batched to avoid overwhelming server)
            const componentsToFetch = existingComponents
                .filter((c) => c.filePath && !fileStates[c.id]?.code && !c.code);
            const BATCH_SIZE = 5;
            const fetchedCodes = [];
            for (let i = 0; i < componentsToFetch.length; i += BATCH_SIZE) {
                const batch = componentsToFetch.slice(i, i + BATCH_SIZE);
                const batchResults = await Promise.all(
                    batch.map((c) =>
                        fetchComponentCode({ id: c.id, environmentName: selectedEnvironment })
                            .then((res) => ({ id: c.id, code: res?.data?.code || "" }))
                    )
                );
                fetchedCodes.push(...batchResults);
            }
            const codeMap = Object.fromEntries(fetchedCodes.map((f) => [f.id, f.code]));

            // Add files to zip
            existingComponents.forEach((component) => {
                const code = fileStates[component.id]?.code || component.code || codeMap[component.id];
                if (component.filePath && code) {
                    const cleanPath = component.filePath.startsWith("/")
                        ? component.filePath.slice(1)
                        : component.filePath;
                    zip.file(cleanPath, code);
                }
            });

            // Generate zip
            const blob = await zip.generateAsync({ type: "blob" });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${workflow?.name || "project"}-export.zip`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast.success(
                t("appPreview.exportSuccess") ||
                    "Project exported successfully",
                {
                    id: toastId,
                }
            );

            logExportAuditCall({
                resourceType: "project",
                resourceId: workflow?.id || "",
                resourceName: workflow?.name || "project",
                exportFormat: "zip",
                recordCount: existingComponents.length,
            }).then(() => {
                toast.success("Export audited");
            }).catch(() => {});
        } catch (error) {
            console.error("Export error:", error);
            toast.error(
                t("appPreview.exportError") || "Failed to export project",
                {
                    id: toastId,
                }
            );
        }
    }, [stepId, components, t, logExportAuditCall, workflow?.id, workflow?.name, fileStates, fetchComponentCode, selectedEnvironment]);


    // Handle create new file (supports both dialog and inline creation)
    const handleCreateFile = React.useCallback(
        async (fileName, folderPathOverride) => {
            if (!stepId) return;

            const baseFolderPath = folderPathOverride !== undefined
                ? folderPathOverride
                : newFileDialog.folderPath;

            try {
                // Parse fileName to support nested folders (e.g., "folder/subfolder/file.js")
                const pathParts = fileName.split("/");
                const actualFileName = pathParts[pathParts.length - 1];
                const nestedFolders = pathParts.slice(0, -1).join("/");

                // Extract component name (without extension) and file extension
                const hasExtension = actualFileName.includes(".");
                const extension = hasExtension
                    ? actualFileName.substring(actualFileName.lastIndexOf("."))
                    : ".jsx";
                let rawName = hasExtension
                    ? actualFileName.substring(0, actualFileName.lastIndexOf("."))
                    : actualFileName;

                // Auto-PascalCase for jsx/tsx files (React components must start uppercase)
                const isReactFile = [".jsx", ".tsx"].includes(extension) || !hasExtension;
                let componentName = rawName;
                if (isReactFile && rawName) {
                    const isHook = rawName.toLowerCase().startsWith("use");
                    if (isHook) {
                        const afterUse = rawName.substring(3);
                        const pascalAfterUse = afterUse
                            .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
                            .replace(/^[a-z]/, (chr) => chr.toUpperCase())
                            .replace(/[^a-zA-Z0-9]/g, "");
                        componentName = `use${pascalAfterUse}`;
                    } else {
                        componentName = rawName
                            .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
                            .replace(/^[a-z]/, (chr) => chr.toUpperCase())
                            .replace(/[^a-zA-Z0-9]/g, "");
                    }
                }

                // Build the full file path
                const fullFileName = `${componentName}${extension}`;

                // Combine base folder path, nested folders, and filename
                let filePath = fullFileName;
                if (nestedFolders) {
                    filePath = `${nestedFolders}/${fullFileName}`;
                }
                if (baseFolderPath) {
                    filePath = `${baseFolderPath}/${filePath}`;
                }

                // Create appropriate content based on file type
                let defaultCode;
                if (
                    extension === ".jsx" ||
                    extension === ".js" ||
                    extension === ".tsx" ||
                    extension === ".ts"
                ) {
                    // Create a basic React component template for JS/JSX files
                    defaultCode = `import React from 'react';

export default function ${componentName}() {
    return (
        <div className="p-4">
            <h1 className="text-2xl font-bold">${componentName}</h1>
            <p>Your component content goes here.</p>
        </div>
    );
}
`;
                } else {
                    // For non-JS files (JSON, MD, etc.), create empty content
                    defaultCode = "";
                }

                await createComponentApi({
                    stepId,
                    name: fullFileName, // Use the full filename with extension
                    environmentName: selectedEnvironment,
                    type:
                        filePath.endsWith(".jsx") &&
                        (filePath.includes("pages") ||
                            filePath.includes(".page"))
                            ? "PAGE"
                            : filePath.includes("entities")
                            ? "ENTITY"
                            : "COMPONENT",
                    code: defaultCode,
                    description: `${componentName} component`,
                    filePath, // Add filePath as top-level field
                    metadata: {
                        filePath,
                        isNonJsFile:
                            extension !== ".jsx" &&
                            extension !== ".js" &&
                            extension !== ".tsx" &&
                            extension !== ".ts",
                    },
                });

                toast.success(
                    t("appPreview.fileCreated") ||
                        `${componentName} created successfully`
                );

                // Refresh components list
                fetchComponents();

                // Close dialog
                setNewFileDialog({ isOpen: false, folderPath: null });

                // Force iframe reload to reflect new component
                reloadIframe();
            } catch (error) {
                console.error("Error creating file:", error);
                toast.error(
                    error?.message ||
                        t("appPreview.fileCreationError") ||
                        "Failed to create file"
                );
            }
        },
        [
            stepId,
            fetchComponents,
            selectedEnvironment,
            newFileDialog,
            reloadIframe,
            t,
        ]
    );

    // Handle create new folder (supports both dialog and inline creation)
    const handleCreateFolder = React.useCallback(
        async (folderName, parentPathOverride) => {
            if (!stepId) return;

            const parentPath = parentPathOverride !== undefined
                ? parentPathOverride
                : newFolderDialog.parentPath;

            try {
                const fullFolderPath = parentPath
                    ? `${parentPath}/${folderName}`
                    : folderName;

                // Create a placeholder file in the new folder
                // This ensures the folder appears in the file tree
                const placeholderName = ".gitkeep";
                const filePath = `${fullFolderPath}/${placeholderName}`;

                // Create a minimal placeholder component
                const placeholderCode = `// Placeholder file to create folder structure
export default function Placeholder() {
    return null;
}
`;

                await createComponentApi({
                    stepId,
                    name: `${folderName}Placeholder`,
                    environmentName: selectedEnvironment,
                    type: "COMPONENT",
                    code: placeholderCode,
                    description: `Placeholder for ${fullFolderPath} folder`,
                    filePath,
                    metadata: {
                        filePath,
                        isPlaceholder: true,
                    },
                });

                toast.success(
                    t("appPreview.folderCreated") ||
                        `Folder ${folderName} created successfully`
                );

                // Refresh components list
                fetchComponents();

                // Close dialog
                setNewFolderDialog({ isOpen: false, parentPath: null });

                // Navigate to the new folder
                handleFolderSelect(fullFolderPath);
            } catch (error) {
                console.error("Error creating folder:", error);
                toast.error(
                    error?.message ||
                        t("appPreview.folderCreationError") ||
                        "Failed to create folder"
                );
            }
        },
        [
            stepId,
            fetchComponents,
            selectedEnvironment,
            newFolderDialog,
            handleFolderSelect,
            t,
        ]
    );

    // Handle delete file request
    const handleDeleteFile = React.useCallback((filePath, fileId) => {
        // Extract file name from path for display
        const fileName = filePath.split("/").pop();
        setDeleteDialog({
            isOpen: true,
            filePath,
            fileName,
            fileId,
        });
    }, []);

    // Handle confirm delete
    const handleConfirmDelete = React.useCallback(async () => {
        if (!stepId || !deleteDialog.filePath) return;

        try {
            await deleteComponentApi({
                stepId,
                // filePath: deleteDialog.filePath,
                id: deleteDialog.fileId,
                environmentName: selectedEnvironment,
            });

            toast.success(
                t("appPreview.fileDeleted") || "File deleted successfully"
            );

            // Refresh components list
            fetchComponents();

            // Close dialog
            setDeleteDialog({
                isOpen: false,
                filePath: null,
                fileName: null,
                fileId: null,
            });

            // If the deleted file was selected, clear selection
            if (
                selectedComponent?.filePath === deleteDialog.filePath ||
                selectedComponent?.name === deleteDialog.fileName
            ) {
                setSelectedComponent(null);
            }
        } catch (error) {
            console.error("Error deleting file:", error);
            toast.error(
                error?.message ||
                    t("appPreview.fileDeleteError") ||
                    "Failed to delete file"
            );
        }
    }, [
        stepId,
        deleteDialog.filePath,
        deleteDialog.fileId,
        selectedEnvironment,
        fetchComponents,
    ]);

    // Handle rename file
    const handleRenameFile = React.useCallback(
        async (oldPath, fileId, component, newPath) => {
            if (!stepId || !fileId || !newPath) return;

            try {
                // Extract filename from newPath
                const newFileName = newPath.split("/").pop();

                // Determine if we should update the name field
                // For JS/TS files, extract component name (without extension) if it's PascalCase
                // For other files, use the full filename
                let newName = newFileName;
                const fileExtension = newFileName.substring(
                    newFileName.lastIndexOf(".")
                );
                const nameWithoutExt = newFileName.substring(
                    0,
                    newFileName.lastIndexOf(".")
                );

                // Check if it's a JS/TS component file and if the name without extension is valid PascalCase
                const isJsFile = [".js", ".jsx", ".ts", ".tsx"].includes(
                    fileExtension
                );
                const isValidPascalCase = /^[A-Z][a-zA-Z0-9]*$/.test(
                    nameWithoutExt
                );

                // If it's a JS file and has valid PascalCase name, use the name without extension
                // Otherwise, use the full filename (this matches how createComponent works)
                if (isJsFile && isValidPascalCase) {
                    newName = nameWithoutExt;
                }

                const response = await updateComponentApi({
                    id: fileId,
                    name: newName,
                    filePath: newPath,
                    environmentName: selectedEnvironment,
                });

                if (response?.status === 200) {
                    toast.success(
                        t("appPreview.fileRenamed") ||
                            "File renamed successfully"
                    );

                    // Refresh components list
                    fetchComponents();

                    // Update selected component if it was the renamed one
                    if (selectedComponent?.id === fileId) {
                        setSelectedComponent({
                            ...selectedComponent,
                            name: newName,
                            filePath: newPath,
                        });
                    }

                    // Force iframe reload to reflect renamed component
                    reloadIframe();
                } else {
                    throw new Error(
                        response?.data?.message ||
                            t("appPreview.fileRenameError") ||
                            "Failed to rename file"
                    );
                }
            } catch (error) {
                console.error("Error renaming file:", error);
                toast.error(
                    error?.message ||
                        t("appPreview.fileRenameError") ||
                        "Failed to rename file"
                );
            }
        },
        [
            stepId,
            fetchComponents,
            selectedEnvironment,
            reloadIframe,
            t,
            updateComponentApi,
        ]
    );

    return (
        <div className="flex flex-col bg-white overflow-hidden min-w-0 w-full flex-1 overflow-y-auto">
            {/* Content Area */}

            {/* Content Area */}
            <div className="flex-1 flex overflow-hidden min-h-0 min-w-0 border-l border-gray-200">
                {markdownPreview ? (
                    <MarkdownPreviewPanel
                        title={markdownPreview.title}
                        content={markdownPreview.content}
                        onClose={closeMarkdownPreview}
                    />
                ) : activeView !== "dashboard" ? (
                    // Preview Section
                    <div
                        className={`w-full h-full md:h-full relative overflow-hidden min-w-0 ${
                            isMobilePreview
                                ? "bg-gray-50 flex justify-center items-start"
                                : ""
                        }`}
                    >
                        <div
                            className={`relative h-full ${
                                isMobilePreview
                                    ? "w-[430px] max-w-full border-x border-gray-200 bg-white shadow-sm"
                                    : "w-full"
                            }`}
                        >
                            {viewAsUser && (
                                <div className="absolute top-3 right-3 z-30 bg-black/70 backdrop-blur-sm text-white text-xs rounded-full py-1 pl-3 pr-1 flex items-center gap-2 shadow-lg">
                                    <Eye className="w-3 h-3 text-amber-400" />
                                    <span>{viewAsUser.username}</span>
                                    <button
                                        onClick={() => setViewAsUser(null)}
                                        className="ml-0.5 p-0.5 rounded-full hover:bg-white/20 transition-colors"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            )}
                            <AppPreviewIframe
                                stepId={stepId}
                                currentVersion={displayVersion}
                                componentServerUrl={componentServerUrl}
                                useCustomDomain={useCustomDomain}
                                selectedPageId={selectedPageId}
                                components={components}
                                selectedEnvironment={selectedEnvironment}
                                previewToken={previewToken}
                                handleErrorLog={handleErrorLog}
                                addConsoleLogToStepExecution={
                                    addConsoleLogToStepExecution
                                }
                                addAxiosResponseToStepExecution={
                                    addAxiosResponseToStepExecution
                                }
                                onAiSuggestion={handleAiSuggestion}
                                onHumanSuggestion={handleHumanSuggestion}
                                updateCurrentPageContext={
                                    updateCurrentPageContext
                                }
                                t={t}
                                isMobilePreview={isMobilePreview}
                                reloadTrigger={reloadTrigger}
                                isSelectMode={isSelectMode}
                                onElementSelected={handleElementSelected}
                                viewAsUserToken={viewAsUser?.token}
                                onPageChanged={setIframePageId}
                            />
                        </div>
                    </div>
                ) : (
                    // Dashboard Section
                    <div className="flex w-full h-full overflow-hidden min-h-0 min-w-0 relative">
                        {/* Mobile Backdrop */}
                        {isMobileSidebarOpen &&
                            dashboardSection !== "editor" && (
                                <div
                                    className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                                    onClick={() =>
                                        setIsMobileSidebarOpen(false)
                                    }
                                    aria-hidden="true"
                                />
                            )}

                        {/* Left Sidebar - Navigation (hidden only in editor mode) */}
                        {dashboardSection !== "editor" && (
                            <div
                                className={`
                                    fixed md:relative inset-y-0 left-0 z-50 md:z-auto
                                    transform transition-transform duration-300 ease-in-out
                                    ${
                                        isMobileSidebarOpen
                                            ? "translate-x-0"
                                            : "-translate-x-full md:translate-x-0"
                                    }
                                    w-64 md:w-auto
                                    bg-white md:bg-transparent
                                    shadow-xl md:shadow-none
                                `}
                            >
                                <ComponentsDashboardNav
                                    dashboardSection={dashboardSection}
                                    selectedRightPanel={selectedRightPanel}
                                    folders={folders}
                                    componentsByFolder={componentsByFolder}
                                    rootComponents={rootComponents}
                                    selectedFolder={selectedFolder}
                                    selectedComponent={selectedComponent}
                                    hasApiFunctions={
                                        Array.isArray(components) &&
                                        components.some(
                                            (c) =>
                                                c.type === "API" ||
                                                c.type === "FUNCTION"
                                        )
                                    }
                                    loading={loading}
                                    error={error}
                                    onBackToMenu={handleBackToMenu}
                                    onBackToEditor={handleBackToEditor}
                                    onEnterEditor={handleEnterEditor}
                                    onEnterOverview={handleEnterOverview}
                                    onEnterUsers={handleEnterUsers}
                                    onEnterDomains={handleEnterDomains}
                                    onEnterAuthentication={
                                        handleEnterAuthentication
                                    }
                                    onEnterDataSecurity={
                                        handleEnterDataSecurity
                                    }
                                    onEnterDatabaseSharing={
                                        handleEnterDatabaseSharing
                                    }
                                    onEnterFunctionSharing={
                                        handleEnterFunctionSharing
                                    }
                                    onEnterIntegrations={
                                        handleEnterIntegrations
                                    }
                                    onEnterSecrets={handleEnterSecrets}
                                    onEnterSettings={handleEnterSettings}
                                    onEnterAPIs={handleEnterAPIs}
                                    onEnterEnvironments={
                                        handleEnterEnvironments
                                    }
                                    onEnterImport={handleEnterImport}
                                    onEnterCodeScanning={handleEnterCodeScanning}
                                    onEnterTesting={handleEnterTesting}
                                    onEnterActivity={navigateToActivity}
                                    onFolderSelect={handleFolderSelect}
                                    onComponentSelect={handleComponentSelect}
                                    onViewVersionHistory={
                                        handleViewVersionHistory
                                    }
                                    onCreateFile={handleCreateFile}
                                    onCreateFolder={handleCreateFolder}
                                    onDeleteFile={handleDeleteFile}
                                    onRenameFile={handleRenameFile}
                                />
                            </div>
                        )}

                        {/* Right Content Area */}
                        <div className="flex-1 flex flex-col bg-white overflow-hidden min-h-0 min-w-0 w-full md:w-auto">
                            {selectedRightPanel === "overview" ? (
                                <OverviewPanel
                                    databases={databases}
                                    onOpenApp={handleOpenApp}
                                    workflowName={workflow?.name || ""}
                                    workflowDescription={
                                        workflow?.description || ""
                                    }
                                    workflowIcon={workflow?.iconUrl || null}
                                    isUpdatingWorkflow={isLoadingUpdateWorkflow}
                                    onUpdateWorkflow={
                                        handleUpdateWorkflowMetadata
                                    }
                                    isTokenPublic={workflow?.isTokenPublic}
                                    onEnterIntegrations={() => {
                                        setSelectedRightPanel("integrations");
                                    }}
                                    onSwitchPanel={(panel) => setSelectedRightPanel(panel)}
                                    maintainableBlueprint={maintainableBlueprint}
                                    blueprintSubscription={blueprintSubscription}
                                    onBlueprintCreated={refetchMaintainableBlueprint}
                                />
                            ) : selectedRightPanel === "version-history" ? (
                                <VersionHistoryPanel
                                    versionHistory={versionHistory}
                                    isLoading={isLoadingStepVersions}
                                    isLoadingRollback={isLoadingRollback}
                                    onRollback={handleRollback}
                                    onUpdateVersion={handleUpdateVersion}
                                    hasMore={hasMoreVersions}
                                    missingSecretWarnings={missingSecretWarnings}
                                    onDismissWarnings={() => setMissingSecretWarnings([])}
                                    versionPage={versionPage}
                                    onLoadMore={handleLoadMoreVersions}
                                    selectedEnvironment={selectedEnvironment}
                                    stepId={stepId}
                                />
                            ) : selectedRightPanel === "users" ? (
                                <UsersPanel
                                    workflowId={workflow?.id}
                                    requireLogin={requireLogin}
                                />
                            ) : selectedRightPanel === "domains" ? (
                                <DomainsPanel
                                    isCustomDomainFeatureEnabled={isFeatureEnabled(
                                        "customDomains"
                                    )}
                                    workflowCustomDomain={
                                        workflow?.customDomain || null
                                    }
                                    workflowInternalDomain={
                                        workflow?.internalDomain || null
                                    }
                                    isUpdatingWorkflow={isLoadingUpdateWorkflow}
                                    onUpdateWorkflow={
                                        handleUpdateWorkflowMetadata
                                    }
                                />
                            ) : selectedRightPanel === "integrations" ? (
                                <IntegrationsPanel workflowId={workflow?.id} />
                            ) : selectedRightPanel === "authentication" ? (
                                <AuthenticationPanel
                                    requireLogin={requireLogin}
                                    requiresPartialLogin={requiresPartialLogin}
                                    allowSelfSignup={allowSelfSignup}
                                    isUpdatingVisibility={isUpdatingVisibility}
                                    onVisibilityToggle={handleVisibilityToggle}
                                    onPartialLoginToggle={
                                        handlePartialLoginToggle
                                    }
                                    onSelfSignupToggle={handleSelfSignupToggle}
                                    isUpdatingSettings={isUpdatingVisibility}
                                    mfaEnabled={mfaEnabled}
                                    onMfaToggle={handleMfaToggle}
                                    hasCustomLoginPage={hasCustomLoginPage}
                                    ssoEnabled={ssoEnabled}
                                    ssoRoleMappings={ssoRoleMappings}
                                    ssoClaimMappings={ssoClaimMappings}
                                    onSSOToggle={handleSSOToggle}
                                    onRoleMappingsChange={
                                        handleRoleMappingsChange
                                    }
                                    onClaimMappingsChange={
                                        handleClaimMappingsChange
                                    }
                                    isSSOFeatureEnabled={isFeatureEnabled(
                                        "sso"
                                    )}
                                />
                            ) : selectedRightPanel === "data-security" ? (
                                <DataSecurityPanel
                                    workflowId={workflow?.id}
                                    componentsResponse={components}
                                    requiresLogin={workflow?.requiresLogin}
                                />
                            ) : selectedRightPanel === "database-sharing" ? (
                                <DatabaseSharingPanel
                                    workflowId={workflow?.id}
                                />
                            ) : selectedRightPanel === "function-sharing" ? (
                                <FunctionSharingPanel
                                    workflowId={workflow?.id}
                                />
                            ) : selectedRightPanel === "secrets" ? (
                                <SecretsPanel />
                            ) : selectedRightPanel === "settings" ? (
                                <SettingsPanel
                                    isProductionLockFeatureEnabled={isFeatureEnabled(
                                        "productionLock"
                                    )}
                                    isFunctionAPIEnabled={isFunctionAPIEnabled}
                                    onFunctionAPIToggle={
                                        handleFunctionAPIToggle
                                    }
                                    isUpdatingSettings={
                                        isUpdatingVisibility ||
                                        isTogglingProductionLock
                                    }
                                    onExportProject={handleExportProject}
                                    hasBackendFunctionsAccess={isFeatureEnabled(
                                        "backendFunctions"
                                    )}
                                    hasExportAccess={isFeatureEnabled(
                                        "exportApp"
                                    )}
                                    isProductionLocked={isProductionLocked}
                                    onProductionLockToggle={
                                        handleProductionLockToggle
                                    }
                                    isInstallableWebApp={isInstallableWebApp}
                                    onInstallableWebAppToggle={
                                        handleInstallableWebAppToggle
                                    }
                                    isOfflineEnabled={isOfflineEnabled}
                                    onOfflineToggle={handleOfflineToggle}
                                    isDeepLinkEnabled={isDeepLinkEnabled}
                                    onDeepLinkToggle={handleDeepLinkToggle}
                                    isRegressionTestingEnabled={
                                        workflow?.isRegressionTestingEnabled || false
                                    }
                                    onRegressionTestingToggle={
                                        handleRegressionTestingToggle
                                    }
                                    gitHubRepo={gitHubRepo}
                                    onGitHubSettingsChange={
                                        handleGitHubSettingsChange
                                    }
                                    isGitHubConnected={isGitHubConnected}
                                    onGitHubConnect={handleGitHubConnect}
                                    workflowId={workflow?.id}
                                />
                            ) : selectedRightPanel === "apis" ? (
                                <APIsPanel
                                    components={components || []}
                                    workflowCustomDomain={
                                        workflow?.customDomain || null
                                    }
                                    workflowInternalDomain={
                                        workflow?.internalDomain || null
                                    }
                                    userServiceKey={
                                        workflow?.userServiceKey || null
                                    }
                                    isFunctionAPIEnabled={isFunctionAPIEnabled}
                                    onRefresh={fetchComponents}
                                    onAuditKeyView={handleAuditKeyView}
                                />
                            ) : selectedRightPanel === "environments" ? (
                                <EnvironmentsPanel
                                    isEnvironmentsFeatureEnabled={isFeatureEnabled(
                                        "environments"
                                    )}
                                />
                            ) : selectedRightPanel === "import" ? (
                                <ImportProjectPanel
                                    stepId={stepId}
                                    selectedEnvironment={selectedEnvironment}
                                    onImportComplete={handleComponentUpdated}
                                    fetchComponents={fetchComponents}
                                    reloadIframe={reloadIframe}
                                />
                            ) : selectedRightPanel === "code-scanning" ? (
                                <CodeScanningPanel stepId={stepId} selectedEnvironment={selectedEnvironment} />
                            ) : selectedRightPanel === "testing" ? (
                                <TestingPanel
                                    workflowId={workflow?.id}
                                    stepId={stepId}
                                />
                            ) : selectedRightPanel === "activity" ? (
                                <ActivityPanel
                                    workflowId={workflow?.id}
                                    stepId={stepId}
                                />
                            ) : (
                                <CodeEditorPanel
                                    components={components || []}
                                    dashboardSection={dashboardSection}
                                    onComponentUpdated={handleComponentUpdated}
                                    onBackToMenu={handleBackToMenu}
                                    onCreateFile={handleCreateFile}
                                    onCreateFolder={handleCreateFolder}
                                    onDeleteFile={handleDeleteFile}
                                    onRenameFile={handleRenameFile}
                                    hasCodeEditorAccess={isFeatureEnabled(
                                        "inAppCodeEdits"
                                    )}
                                    isEnviromentLocked={
                                        isCurrentEnvironmentLocked
                                    }
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* File/folder creation now happens inline in the file tree (VS Code style) */}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.isOpen}
                onOpenChange={(isOpen) =>
                    !isOpen &&
                    setDeleteDialog({
                        isOpen: false,
                        filePath: null,
                        fileName: null,
                    })
                }
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {t("appPreview.confirmDelete") || "Confirm Delete"}
                        </DialogTitle>
                        <DialogDescription>
                            {t("appPreview.deleteFileMessage", {
                                fileName: deleteDialog.fileName,
                            }) ||
                                `Are you sure you want to delete ${deleteDialog.fileName}? This action cannot be undone.`}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() =>
                                setDeleteDialog({
                                    isOpen: false,
                                    filePath: null,
                                    fileName: null,
                                })
                            }
                            disabled={isDeletingComponent}
                        >
                            {t("appPreview.cancel") || "Cancel"}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={isDeletingComponent}
                        >
                            {isDeletingComponent
                                ? t("common.loading") || "Deleting..."
                                : t("appPreview.delete") || "Delete"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
